import type { ContentCardProps } from '../../types';
import type { CardMetadataItem, IconNames } from '@workramp/ui';
import { Card, Stack, Icon, Col, Body, Toast } from '@workramp/ui';
import { useState } from 'react';
import { useTheme } from 'styled-components';
import { I18n } from '~/common/explicit-exports';
import { addTagsMetadata } from '../../utils';
import { FooterWrapper, FullHeightRow, WindowStatusAnchor } from '../../styles';
import { CardDescription, CardTitle } from '../CardText';
import { CardOverflowMenu } from '../OverflowCardMenu';

export interface LinkCardProps extends Omit<ContentCardProps, 'assignmentStatus' | 'hideContentCTA'> {
  onPress?: () => void;
  metadata?: CardMetadataItem[];
  url: string;
  iconName: IconNames;
  iconColor?: string;
  errorImageUrl: string;
}

export function LinkCard({
  name,
  imageSrc,
  description,
  iconName,
  iconColor,
  errorImageUrl,
  onPress,
  tags = [],
  overflowMenuItems = {},
  metadata = [],
  url,
  actions,
}: LinkCardProps) {
  const VIEW = I18n.t('js.learner.libraries.view');
  const LINK_COPIED = I18n.t('js.learner.libraries.link_copied');

  const theme = useTheme();
  const [isCopied, setIsCopied] = useState(false);

  const tagMetadata = addTagsMetadata([], tags, theme.colors.on[600]);
  const metadataWithTags = [...tagMetadata, ...metadata];

  const showOverflowMenu = Boolean(Object.keys(overflowMenuItems || {}).length);

  return (
    <Card onPress={onPress}>
      {imageSrc && (
        <Card.Hero
          src={imageSrc}
          errorImage={errorImageUrl}
          icon={<Icon color={iconColor} name={iconName} size="lg" />}
        />
      )}
      <Card.Container data-testid="library-card">
        <Card.Content>
          <Card.Body>
            <WindowStatusAnchor href={url} onClick={(e) => e.preventDefault()}>
              <Stack size={4}>
                {!imageSrc && <Card.Icon iconColor={iconColor} iconName={iconName} />}
                <Stack size={2}>
                  <CardTitle text={name} color={theme.colors.on[900]} />
                  {metadataWithTags.length ? <Card.Metadata metadata={metadataWithTags} /> : null}
                  <CardDescription text={description} color={theme.colors.on[500]} />
                </Stack>
              </Stack>
            </WindowStatusAnchor>
          </Card.Body>
        </Card.Content>
      </Card.Container>
      <Card.Footer size={0}>
        <FooterWrapper>
          {showOverflowMenu ? (
            <FullHeightRow align="center" justify="flex-end">
              <Col xs="auto">
                {actions}
                <CardOverflowMenu overflowMenuItems={overflowMenuItems} />
              </Col>
            </FullHeightRow>
          ) : (
            <FullHeightRow align="center" justify="space-between">
              <Col xs="auto">
                <Body color={theme.colors.on[600]} level="md" unselectable="on">
                  {VIEW}
                </Body>
              </Col>
              <Col xs="auto">{actions}</Col>
            </FullHeightRow>
          )}
        </FooterWrapper>
      </Card.Footer>

      <Toast isOpen={isCopied} onClose={() => setIsCopied(false)}>
        {LINK_COPIED}
      </Toast>
    </Card>
  );
}
